import { NodeObject } from 'react-force-graph-2d';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ColorBy = 'none' | 'category' | 'community' | 'sub_community';
export type SizeBy = 'uniform' | 'clicks' | 'impressions';

export interface FGNode extends NodeObject {
  id: string;
  title: string;
  category: string | null;
  pillar: string | null;
  clicks: number;
  impressions: number;
  avg_position: number | null;
  community: number | null;
  sub_community: number | null;
  isOrphan: boolean;
}

export interface FGLink {
  source: string | FGNode;
  target: string | FGNode;
  labels: string[];
}

// ---------------------------------------------------------------------------
// Color palettes
// ---------------------------------------------------------------------------

const COMMUNITY_COLORS = [
  '#01bcd4', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981',
  '#3b82f6', '#ec4899', '#06b6d4', '#84cc16', '#f97316',
  '#6366f1', '#14b8a6', '#e11d48', '#a855f7', '#64748b',
];

const CATEGORY_COLORS: Record<string, string> = {
  'Corporate Training': '#1a2332',
  'Communication Skills': '#01bcd4',
  'Emails and Writing': '#3b82f6',
  'Grammar and Vocabulary': '#8b5cf6',
  'Leadership': '#f59e0b',
  'Meetings': '#10b981',
  'Presentations': '#ef4444',
  'Speaking': '#ec4899',
  'Culture': '#06b6d4',
};

const FALLBACK_COLORS = ['#6366f1', '#14b8a6', '#f97316', '#a855f7', '#64748b', '#84cc16', '#0ea5e9', '#e11d48'];
let _catIdx = 0;
const _dynCatColors: Record<string, string> = {};

export const getCategoryColor = (cat: string | null): string => {
  if (!cat) return '#94a3b8';
  if (CATEGORY_COLORS[cat]) return CATEGORY_COLORS[cat];
  if (!_dynCatColors[cat]) {
    _dynCatColors[cat] = FALLBACK_COLORS[_catIdx % FALLBACK_COLORS.length];
    _catIdx++;
  }
  return _dynCatColors[cat];
};

export const getCommunityColor = (id: number | null): string => {
  if (id == null) return '#94a3b8';
  return COMMUNITY_COLORS[id % COMMUNITY_COLORS.length];
};

// ---------------------------------------------------------------------------
// Node radius
// ---------------------------------------------------------------------------

export const getNodeRadius = (node: FGNode, sizeBy: SizeBy): number => {
  if (sizeBy === 'uniform') return 5;
  const val = sizeBy === 'clicks' ? node.clicks : node.impressions;
  if (val <= 0) return 3;
  return Math.max(3, Math.min(20, 2 + Math.log10(val + 1) * 4));
};

// ---------------------------------------------------------------------------
// HTML entity decoder
// ---------------------------------------------------------------------------

export const decodeHtml = (s: string): string =>
  s.replace(/&#038;/g, '&').replace(/&amp;/g, '&').replace(/&#8217;/g, '\u2019')
    .replace(/&#8216;/g, '\u2018').replace(/&quot;/g, '"').replace(/&hellip;/g, '\u2026')
    .replace(/&#8211;/g, '\u2013').replace(/&#8212;/g, '\u2014')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>');
